import { z } from 'zod'
import { onlinePaymentMethodSchema, outsidePaymentMethodSchema } from '../enums/sales-payment.enum.js'
import { calendarDaySchema } from '../validators/temporal.validator.js'

/** Starting a payment from its link (docs/sales.spec.md, "Paying"). */
export const paymentIntentInputSchema = z
  .object({
    method: onlinePaymentMethodSchema,
    payerName: z.string().trim().min(1, 'Enter your name.').max(120, 'That name is too long.'),
    payerEmail: z.string().trim().email('Enter an email address for your receipt.').max(254),
    /** Save the method to pay later payments automatically: the deposit only, and only signed in. */
    saveForLater: z.boolean().optional(),
  })
  .strict()
export type PaymentIntentInput = z.infer<typeof paymentIntentInputSchema>

/** A payment staff received outside the app: a check, a wire, cash. */
export const outsidePaymentInputSchema = z
  .object({
    method: outsidePaymentMethodSchema,
    amountCents: z.number().int().min(1, 'Enter the amount received.').max(100_000_000, 'That amount is too high.'),
    receivedOn: calendarDaySchema,
    reference: z.string().trim().max(120, 'That reference is too long.'),
  })
  .strict()
export type OutsidePaymentInput = z.infer<typeof outsidePaymentInputSchema>

const reason = z.string().trim().min(1, 'Say why: the couple are told.').max(500, 'Keep the reason under 500 characters.')

/**
 * Give money back from one payment record (docs/sales.spec.md, "Cancellations,
 * refunds and security deposits"). What's left to give back is checked by the
 * API, against the record.
 */
export const refundInputSchema = z
  .object({
    amountCents: z.number().int().min(1, 'Enter the amount to refund.').max(100_000_000, 'That amount is too high.'),
    reason,
  })
  .strict()
export type RefundInput = z.infer<typeof refundInputSchema>

/** Settle a security deposit after the event: return some, all, or none of it (keep it), saying why. */
export const depositReturnInputSchema = z
  .object({
    amountCents: z.number().int().min(0, 'Enter the amount to return, or 0 to keep it.').max(100_000_000, 'That amount is too high.'),
    reason,
  })
  .strict()
export type DepositReturnInput = z.infer<typeof depositReturnInputSchema>
